import { labDayOf } from '@/domain';

/**
 * Every instant on the wire is UTC with a Z suffix, section 7.9. Every instant
 * on a screen is Dubai time.
 *
 * Pinned to the lab's zone rather than the device's, deliberately. A handset
 * bought abroad or set wrong still shows the time on the wall of the lab, and
 * a manager reading a report from home sees the same clock the floor sees. A
 * screen that quietly shifted by the reader's own offset would have two people
 * disagreeing about when a case was finished.
 *
 * The previous code sliced the ISO string, which prints UTC. Dubai is four
 * hours ahead, so every timestamp in the system was four hours early. The
 * client noticed.
 */
export const LAB_TIME_ZONE = 'Asia/Dubai';

const parts = new Intl.DateTimeFormat('en-GB', {
  timeZone: LAB_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

function pieces(iso: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const part of parts.formatToParts(new Date(iso))) out[part.type] = part.value;
  return out;
}

/** `2026-08-06 13:15`, in lab time. */
export function formatInstant(iso: string): string {
  const p = pieces(iso);
  return `${p['year'] ?? ''}-${p['month'] ?? ''}-${p['day'] ?? ''} ${p['hour'] ?? ''}:${p['minute'] ?? ''}`;
}

/** `13:15`, in lab time. */
export function formatTime(iso: string): string {
  const p = pieces(iso);
  return `${p['hour'] ?? ''}:${p['minute'] ?? ''}`;
}

/**
 * `2026-08-06`, the lab's calendar day the instant fell on.
 *
 * Not `iso.slice(0, 10)`: a case dispatched at two in the morning Dubai time
 * is still the previous day in UTC, and the archive would file it under the
 * wrong date.
 *
 * Delegated to the domain rather than formatted here, so the day a screen
 * prints and the day the productivity report counts are the same day by
 * construction. Section 3.4 of the request of 20 September 2026 measures the
 * range in lab days, and two definitions of the lab day would eventually
 * disagree about a case finished at half past midnight.
 */
export function formatDay(iso: string): string {
  return labDayOf(iso);
}

/**
 * Today on the lab's calendar, YYYY-MM-DD: what a due date is measured
 * against. The rules in src/domain take it as an argument and never read a
 * clock; a screen reads it here as it draws. The request of 7 October 2026,
 * section 6.
 */
export function labToday(): string {
  return labDayOf(Date.now());
}

const weekday = new Intl.DateTimeFormat('en-GB', { timeZone: LAB_TIME_ZONE, weekday: 'long' });

/** Whole lab days between two `YYYY-MM-DD` days. */
function daysBetween(earlier: string, later: string): number {
  return Math.round((Date.parse(`${later}T00:00:00Z`) - Date.parse(`${earlier}T00:00:00Z`)) / 86_400_000);
}

/**
 * A message's time the way people say it: `13:15` today, `Yesterday 13:15`,
 * `Monday 13:15` within the week, and the full date and time beyond that, all
 * in lab time. Request of 5 October 2026, for Lab Notes.
 */
export function formatRecent(iso: string, now: Date = new Date()): string {
  const days = daysBetween(formatDay(iso), formatDay(now.toISOString()));
  if (days <= 0) return formatTime(iso);
  if (days === 1) return `Yesterday ${formatTime(iso)}`;
  if (days < 7) return `${weekday.format(new Date(iso))} ${formatTime(iso)}`;
  return formatInstant(iso);
}

const shortDay = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', day: '2-digit', month: 'short' });

/** A calendar day, `YYYY-MM-DD`, as `04 Oct`. Date arithmetic only, no clock. */
export function formatShortDay(day: string): string {
  return shortDay.format(new Date(`${day}T00:00:00Z`));
}

/** Two calendar days as `04 Oct \u2013 10 Oct`, or one day alone when they are the same. */
export function formatDaySpan(from: string, to: string): string {
  return from === to ? formatShortDay(from) : `${formatShortDay(from)} \u2013 ${formatShortDay(to)}`;
}
